import {
  Divider,
  Box,
  Hidden,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material';
import {
  GenUnitCard,
  CollapsibleTable,
  WorkOrderStepper,
  GenSnackBar,
} from '@pinnacleautomotive/clarity-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import carPlaceholder from '../../../assets/carPlaceholder.png';
import Empty from '../../../assets/emptyUnapproved.png';
import RequesterDetails from '../../../components/RequesterDetails';
import ServicesDetails from '../../../components/ServicesDetails';
import { CommonColumns } from '../../../containers/orders/commons';
import { hasDealerShipManagerRole } from '../../../helpers/users';
import {
  cancelStepChangeStatusAllOrders,
  getAllOrders,
  hideCancelConfirmation,
} from '../../../redux/actions/workQueue/allOrdersActions';
import { selectCurrentUserRole } from '../../../redux/selectors/userDataSelectors';
import {
  selectAllOrders,
  selectHasMore,
  selectIsLoading,
  selectCurrentPage,
  isChangeStepStatusApiLoading,
  selectShowCancelSuccesful,
  selectShowCancelError,
} from '../../../redux/selectors/workQueue/allOrdersSelectors';
import EmptyState from '../../orders/components/EmptyState';

const AllOrders = ({
  searchQuery,
  companyId,
  selectedVendor,
  selectedDepartments,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('orders');
  const orders = useSelector(selectAllOrders);

  const hasMore = useSelector(selectHasMore);
  const loading = useSelector(selectIsLoading);
  const currentPage = useSelector(selectCurrentPage);

  const userRoles = useSelector(selectCurrentUserRole);
  const showCancelSuccesSnackBar = useSelector(selectShowCancelSuccesful);
  const showCancelErrorSnackBar = useSelector(selectShowCancelError);
  const isChangeStepOrderApiLoading = useSelector(isChangeStepStatusApiLoading);

  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    dispatch(
      getAllOrders({
        companyId,
        pageNumber: 1,
        searchQuery,
        selectedVendor,
        selectedDepartments,
      })
    );
  }, [dispatch, searchQuery, companyId, selectedVendor, selectedDepartments]);

  const loadMoreData = () => {
    dispatch(
      getAllOrders({
        companyId,
        pageNumber: currentPage + 1,
        searchQuery,
        selectedVendor,
      })
    );
  };

  const columns = CommonColumns(t, mediumScreen, []);
  columns[0] = columns.splice(2, 1, columns[0])[0];

  const handleCancelStatus = ({ vendorOrderId, cancellingNote }) => {
    dispatch(
      cancelStepChangeStatusAllOrders({ vendorOrderId, cancellingNote })
    );
  };

  const dispatchHideCancelConfirmation = () => {
    dispatch(hideCancelConfirmation());
  };

  return (
    <>
      <GenSnackBar
        title={t('cancelConfirmation')}
        open={showCancelSuccesSnackBar}
        onClose={() => dispatchHideCancelConfirmation()}
      />
      <GenSnackBar
        title={t('cancelError')}
        open={showCancelErrorSnackBar}
        type="error"
        onClose={() => dispatchHideCancelConfirmation()}
      />
      <CollapsibleTable
        columns={columns}
        rows={orders}
        hasNextPage={hasMore}
        loading={loading}
        onLoadMore={() => loadMoreData()}
        onClick={workOrder =>
          history.push({
            pathname: `/workQueue/orders/${workOrder.vendorOrderId}`,
          })
        }
        collpasibleGetter={data => (
          <Box display="grid">
            <Box p={2} order={{ md: 2 }}>
              <Typography variant="subtitle2" color="textPrimary">
                {t('processStep.title')}
              </Typography>
            </Box>
            <Box p={2} mb={1} order={{ md: 3 }}>
              <WorkOrderStepper
                isVendor={false}
                isManager={hasDealerShipManagerRole([userRoles.roleName])}
                vendorOrderId={data.vendorOrderId}
                steps={data.workFlowHistory}
                statusCancel={handleCancelStatus}
                isApiLoading={isChangeStepOrderApiLoading}
                detail={true}
              />
            </Box>
            <Box pt={2}>
              <Hidden mdUp>
                <Divider />
              </Hidden>
              <GenUnitCard
                order={data}
                image={carPlaceholder}
                showStockAndWoNumber={false}
              >
                <ServicesDetails data={data} />
                <Divider />
                <RequesterDetails data={data} />
              </GenUnitCard>
            </Box>
          </Box>
        )}
      />
      <EmptyState
        show={!loading && !orders.length}
        img={Empty}
        title={t('global:noWO')}
        subtitle={t('global:noWOWorkQueue')}
      />
    </>
  );
};

export default AllOrders;
